import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { 
  Building2,
  FileText,
  ExternalLink,
  User,
  ChevronDown,
  LogOut,
  UserCheck,
  ArrowLeft
} from 'lucide-react';

// Using relative URLs

const AuditorDashboard = () => {
  const navigate = useNavigate();
  const { user, logout, emulateCustomer, returnToAdmin } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Check if super admin is emulating
  const isEmulating = localStorage.getItem('emulated_user') === 'true';

  useEffect(() => {
    fetchDashboard();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`/api/auditor/dashboard`, getAuthHeaders());
      setDashboard(response.data);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
      
      // Check if it's an authentication error
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('Authentication failed. Please login again.');
        setTimeout(() => {
          localStorage.removeItem('token');
          window.location.href = '/login';
        }, 2000);
      } else {
        toast.error(error.response?.data?.detail || 'Failed to load dashboard');
      }
      
      setDashboard(null);
    } finally {
      setLoading(false);
    }
  };

  const handleAccessAudit = (auditId) => {
    // Navigate directly to the audit detail page
    // Auditor will have read-only access with commenting capability
    navigate(`/audit/${auditId}`);
  };

  const getStatusColor = (status) => {
    const colors = {
      in_progress: 'bg-blue-100 text-blue-700',
      completed: 'bg-green-100 text-green-700',
      under_review: 'bg-yellow-100 text-yellow-700',
      certified: 'bg-emerald-100 text-navy-700'
    };
    return colors[status] || 'bg-slate-100 text-slate-700';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-appbg">
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-appbg">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Auditor Dashboard</h1>
              <p className="text-sm text-slate-600">Your assigned customers and audits</p>
            </div>
            
            <div className="flex items-center space-x-4">
              {/* Return to Admin - only show when emulating */}
              {isEmulating && (
                <button
                  onClick={returnToAdmin}
                  className="text-sm font-medium text-purple-600 hover:text-purple-700 flex items-center gap-2 transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Return to Admin
                </button>
              )}
              
              <Badge className="bg-indigo-100 text-indigo-700 border-indigo-200 hover:bg-indigo-100 !important hover:text-indigo-700 !important">
                <UserCheck className="w-3 h-3 mr-1" />
                Auditor
              </Badge>
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="flex items-center gap-2 border-slate-200 hover:bg-slate-50 hover:text-slate-900"
                  >
                    <User className="w-4 h-4 text-slate-600" />
                    <span className="text-sm font-medium text-slate-900 max-w-[150px] truncate">
                      {user?.full_name || user?.email?.split('@')[0]}
                    </span>
                    <ChevronDown className="w-4 h-4 text-slate-500" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium text-slate-900">{user?.email}</p>
                      <p className="text-xs text-slate-500">Auditor</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  
                  {/* My Profile */}
                  <DropdownMenuItem 
                    onClick={() => navigate('/profile')}
                    className="cursor-pointer"
                  >
                    <User className="w-4 h-4 mr-2" />
                    My Profile
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  
                  <DropdownMenuItem 
                    onClick={() => {
                      logout();
                      navigate('/login');
                    }}
                    className="text-red-600 focus:text-red-600 cursor-pointer"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardHeader className="pb-3">
              <CardDescription>Assigned Customers</CardDescription>
              <CardTitle className="text-3xl">{dashboard?.total_customers || 0}</CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardDescription>Total Audits</CardDescription>
              <CardTitle className="text-3xl">{dashboard?.total_audits || 0}</CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardDescription>Active Audits</CardDescription>
              <CardTitle className="text-3xl">
                {dashboard?.customers?.reduce((sum, c) => 
                  sum + c.audits.filter(a => a.status === 'in_progress').length, 0
                ) || 0}
              </CardTitle>
            </CardHeader>
          </Card>
        </div>

        {/* Customers List */}
        {!dashboard?.customers || dashboard.customers.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Building2 className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">No Customers Assigned</h3>
              <p className="text-slate-600">
                You haven't been assigned to any customers yet. Contact your Super Admin.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {dashboard.customers.map((customer) => (
              <Card key={customer.workspace_id}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-xl">{customer.workspace_name}</CardTitle>
                      {customer.domain && (
                        <CardDescription className="mt-1">{customer.domain}</CardDescription>
                      )}
                    </div>
                    {/* Access buttons moved to individual audits */}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="mb-4">
                    <p className="text-sm text-slate-600 mb-3">
                      {customer.audits_count} audit{customer.audits_count !== 1 ? 's' : ''} assigned to you
                    </p>
                  </div>
                  
                  {customer.audits && customer.audits.length > 0 && (
                    <div className="space-y-3">
                      {customer.audits.map((audit) => (
                        <div 
                          key={audit.id}
                          className="p-4 bg-slate-50 rounded-lg border border-slate-200"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h4 className="font-medium text-slate-900 mb-1">{audit.name}</h4>
                              <div className="flex flex-wrap gap-2 mb-2">
                                {audit.frameworks?.map((fw, idx) => (
                                  <Badge key={idx} variant="outline" className="text-xs">
                                    {fw}
                                  </Badge>
                                ))}
                              </div>
                              <div className="flex items-center gap-4">
                                <Badge className={getStatusColor(audit.status)}>
                                  {audit.status.replace('_', ' ')}
                                </Badge>
                                <div className="flex items-center gap-2">
                                  <div className="w-24 bg-slate-200 rounded-full h-2">
                                    <div 
                                      className="bg-navy-600 h-2 rounded-full"
                                      style={{width: `${audit.progress}%`}}
                                    ></div>
                                  </div>
                                  <span className="text-xs text-slate-600">{audit.progress}%</span>
                                </div>
                              </div>
                            </div>
                            <Button
                              onClick={() => handleAccessAudit(audit.id)}
                              className="bg-indigo-600 hover:bg-indigo-700 ml-4"
                              size="sm"
                            >
                              <FileText className="w-4 h-4 mr-2" />
                              Access Audit
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AuditorDashboard;
